/* eslint-disable react/jsx-props-no-spreading, @stylistic/max-len */
import styled from '@emotion/styled';
import {Color, useThemeColors} from '@nfq/colors';
import {Col, Container, Row, Spacer} from '@nfq/react-grid';

import {List} from 'UI/components/layout/List';
import {NumberedList} from 'UI/components/layout/NumberedList';
import {Section} from 'UI/components/layout/Section';
import {H2, H6, P} from 'UI/components/layout/Text';

import {SonosLayout} from 'UI/layouts/SonosLayout';

import type {NextPageWithLayout} from 'types/global';

/**
 * The `Impressum` component is a Next.js page component.
 * It is used as a route and can be accessed throught the nextjs router.
 *
 * @returns A ReactNode representing the `Impressum` page.
 */
const Impressum: NextPageWithLayout = () => {
    const colors = useThemeColors();

    return (
        <Section>
            <Spacer
                y={{
                    xs: 20, md: 10
                }}
                isNotStretching
            />
            <Container>
                <StyledRow justify="center">
                    <Col xl={10} xs={12}>
                        <H2 as="h1">Datenschutzhinweise</H2>
                    </Col>
                    <Col xl={10} xs={12}>
                        <H6 as="h2">
                            Allgemeine Hinweise
                        </H6>
                        <Spacer y={3} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Der Schutz Ihrer persönlichen Daten ist uns ein besonderes Anliegen. Wir verarbeiten Ihre Daten daher ausschließlich auf Grundlage der gesetzlichen Bestimmungen (DSGVO, DDG).<br />
                                <br />
                                In diesen Datenschutzhinweisen informieren wir, die SONOS EUROPE B.V., Groest 106, 1211 EE Hilversum, Niederlande, privacy@sonos.com Sie über die Verarbeitungen personenbezogener Daten, für die wir Verantwortlicher im Sinne der EU-Datenschutzgrundverordnung (DSGVO) sind.<br />
                                <br />
                                Der Datenschutzbeauftragte der Sonos Europe B.V. ist unter der folgenden Adresse erreichbar: Groest 106, 1211 EE Hilversum, Niederlande, oder per E-Mail an privacy@sonos.com.<br />
                                <br />
                                Sofern im Text der Begriff “Daten” verwendet wird, sind jeweils allein personenbezogene Daten im Sinne der DS-GVO gemeint.
                            </Color>
                        </P>
                    </Col>
                    <Col xl={10} xs={12}>
                        <H6 as="h2">
                            1. Datenverarbeitung beim Besuch der Webseite
                        </H6>
                        <Spacer y={3} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Bei der Nutzung der Webseite werden durch den auf dem Endgerät zum Einsatz kommenden Browser technisch bedingt bestimmte Informationen an den Server unserer Webseite gesendet. Diese Daten werden auf unserem Server gespeichert und verarbeitet.
                            </Color>
                        </P>
                        <Spacer y={6} isNotStretching />
                        <NumberedList color={colors.secondary} type="i">
                            <P $size="medium" as="li">
                                Zwecke der Datenverarbeitung sind die Bereitstellung der von Ihnen aufgerufenen Inhalte der Webseite, die Gewährleistung der Sicherheit der eingesetzten IT-Infrastruktur und die Fehlerbehebung. Nutzungsstatistiken werden nur in Form von anonymisierten Zählskripten erhoben. Eine Änderung der Zwecke ist nicht geplant.
                            </P>
                            <P $size="medium" as="li">
                                Die verarbeiteten Daten sind HTTP-Daten: Bei HTTP-Daten handelt es sich um Protokolldaten, die beim Aufruf der Webseite über das Hypertext Transfer Protocol (Secure) (HTTP(S)) technisch bedingt anfallen: Hierzu zählen IP-Adresse, Typ und Version des Internet-Browsers, verwendetes Betriebssystem, die aufgerufene Seite, die übertragene Datenmenge, die zuvor besuchte Seite (Referrer URL), die eingestellte Sprache, Zeitzone, Datum und Uhrzeit des Aufrufs. HTTP(S)-Daten fallen auch auf Servern von unseren Dienstleistern an (z.B. beim Abruf von Drittinhalten).
                            </P>
                            <P $size="medium" as="li">
                                Rechtsgrundlage für die Verarbeitung ist unser berechtigtes Interesse an dem Betrieb der Webseite und der Bereitstellung von Informationen an Interessierte (Art. 6 Abs. 1 lit. f) DSGVO und § 25 Abs. 2 Nr. 2 TTDSG).
                            </P>
                            <P $size="medium" as="li">
                                Die Daten werden automatisch durch den Browser der Nutzerin oder des Nutzers zur Verfügung gestellt.
                            </P>
                            <P $size="medium" as="li">
                                Empfänger der personenbezogenen Daten sind IT-Dienstleister, welche wir im Rahmen einer Vereinbarung zur Auftragsverarbeitung einsetzen. Für das Hosting setzen wir den Dienstleister Amazon Web Services EMEA SARL („AWS“) 38 Avenue John F. Kennedy, L-1855 Luxemburg ein und haben AWS angewiesen einen deutschen Serverstandort für die Datenverarbeitung zu nutzen. AWS kann allerdings im Falle von Störungen und Fehlfunktionen auch aus so genannten Drittländern wie den USA auf die Server in Deutschland zugreifen, um Wartungsarbeiten vorzunehmen. AWS hat zum Schutz Ihrer Daten mit AWS Inc. und weiteren Subunternehmern die EU-Standardvertragsklauseln (2021/914; Modul 3) abgeschlossen. Sie können jederzeit eine Kopie der wesentlichen Vertragsinhalte der Standardvertragsklauseln anfordern. Zudem liegt eine Zertifizierung der Amazon Gesellschaften (u.A. auch der AWS Inc.) nach dem EU-US Data Privacy Framework vor (Art. 45 DSGVO).
                            </P>
                            <P $size="medium" as="li">
                                IP-Adressen werden spätestens nach 24 Stunden anonymisiert. Pseudonyme Nutzungsdaten werden jeweils nach Ablauf von sechs Monaten gelöscht.
                            </P>
                            <P $size="medium" as="li">
                                Ohne Preisgabe personenbezogener Daten ist die Nutzung der Webseite nicht möglich. Eine Kommunikation über die Webseite ist ohne Angaben von Daten technisch nicht möglich.
                            </P>
                        </NumberedList>
                    </Col>
                    <Col xl={10} xs={12}>
                        <H6 as="h2">
                            2. Nutzung der Standortsuche
                        </H6>
                        <Spacer y={3} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Bei der Nutzung der Webseite haben interessierte Personen die Möglichkeit, die Standortsuche für Sonos Verkaufsstellen in ihrer Nähe zu nutzen. Dies ist entweder über die Abfrage des Standorts im Browser oder über direkte Eingabe der Postleitzahl im Abfragefeld auf der Webseite möglich.
                            </Color>
                        </P>
                        <Spacer y={6} isNotStretching />
                        <NumberedList color={colors.secondary} type="i">
                            <P $size="medium" as="li">
                                Zwecke der Datenverarbeitung sind die Bereitstellung einer Standortsuche für Sonos Verkaufsstellen in der Nähe der interessierten Person. Darüber hinaus werden Nutzungsstatistiken der Standortsuche pseudonymisiert erhoben. Eine Änderung der Zwecke ist nicht geplant.
                            </P>
                            <P $size="medium" as="li">
                                Die verarbeiteten Daten sind:
                                <Spacer y={2} isNotStretching />
                                <List color={colors.secondary}>
                                    <P $size="medium" as="li">
                                        HTTP-Daten: Bei HTTP-Daten handelt es sich um Protokolldaten, die beim Aufruf der Webseite über das Hypertext Transfer Protocol (Secure) (HTTP(S)) technisch bedingt anfallen: Hierzu zählen IP-Adresse, Standort, Typ und Version des Internet-Browsers, verwendetes Betriebssystem, die aufgerufene Seite, die übertragene Datenmenge, die zuvor besuchte Seite (Referrer URL), die eingestellte Sprache, Zeitzone, Datum und Uhrzeit des Aufrufs. HTTP(S)-Daten fallen auch auf Servern von unseren Dienstleistern an (z.B. beim Abruf von Drittinhalten).
                                    </P>
                                    <P $size="medium" as="li">
                                        Anzahl der Ergebnisse (Händler im Umkreis von 80 km) und ID des nächstgelegenen Händlers
                                    </P>
                                    <P $size="medium" as="li">
                                        Postleitzahl (bei direkter Eingabe)
                                    </P>
                                </List>
                            </P>
                            <P $size="medium" as="li">
                                Rechtsgrundlage für die Verarbeitung ist unser berechtigtes Interesse an dem Angebot einer Standortsuche für interessierte Personen (Art. 6 Abs. 1 lit. f) DSGVO und § 25 Abs. 2 Nr. 2 TTDSG).
                            </P>
                            <P $size="medium" as="li">
                                Die Daten werden automatisch durch den Browser der Nutzerin oder des Nutzers oder durch direkte Eingabe zur Verfügung gestellt.
                            </P>
                            <P $size="medium" as="li">
                                Empfänger der personenbezogenen Daten sind IT-Dienstleister, welche wir im Rahmen einer Vereinbarung zur Auftragsverarbeitung einsetzen. Für das Hosting setzen wir den Dienstleister Amazon Web Services EMEA SARL („AWS“) 38 Avenue John F. Kennedy, L-1855 Luxemburg ein und haben AWS angewiesen einen deutschen Serverstandort für die Datenverarbeitung zu nutzen. AWS kann allerdings im Falle von Störungen und Fehlfunktionen auch aus so genannten Drittländern wie den USA auf die Server in Deutschland zugreifen, um Wartungsarbeiten vorzunehmen. AWS hat zum Schutz Ihrer Daten mit AWS Inc. und weiteren Subunternehmern die EU-Standardvertragsklauseln (2021/914; Modul 3) abgeschlossen. Sie können jederzeit eine Kopie der wesentlichen Vertragsinhalte der Standardvertragsklauseln anfordern. Zudem liegt eine Zertifizierung der Amazon Gesellschaften (u.A. auch der AWS Inc.) nach dem EU-US Data Privacy Framework vor (Art. 45 DSGVO).
                            </P>
                            <P $size="medium" as="li">
                                IP-Adressen werden spätestens nach 24 Stunden anonymisiert. Die Koordinaten des Standortes werden nicht gespeichert, sondern sofort gelöscht.
                            </P>
                            <P $size="medium" as="li">
                                Ohne Preisgabe personenbezogener Daten ist die Nutzung der Webseite nicht möglich. Eine Nutzung der Standortsuche erfolgt freiwillig und ist nicht verpflichtend.
                            </P>
                        </NumberedList>
                    </Col>
                    <Col xl={10} xs={12}>
                        <H6 as="h2">
                            3. Datensicherheit
                        </H6>
                        <Spacer y={3} isNotStretching />
                        <P $size="medium">
                            <Color $color={colors.secondary}>
                                Unsere Website und die Datenübertragung erfolgen ausschließlich über eine sichere TLS-Verschlüsselung (Transport Layer Security). Dies gewährleistet, dass Ihre Daten während der Übertragung im Internet gegen unbefugten Zugriff geschützt sind.
                            </Color>
                        </P>
                    </Col>
                    <Col xl={10} xs={12}>
                        <H6 as="h2">
                            4. Ihre Rechte und weitere Angaben
                        </H6>
                        <Spacer y={3} isNotStretching />
                        <NumberedList color={colors.secondary} type="1">
                            <P $size="medium" as="li">
                                Wir nutzen keine Verfahren automatisierter Einzelfallentscheidungen.
                            </P>
                            <P $size="medium" as="li">
                                Eine Änderung der vorstehend genannten Zwecke ist nicht geplant.
                            </P>
                            <P $size="medium" as="li">
                                Sollten Ihre personenbezogenen Daten unrichtig oder unvollständig sein, haben Sie ein Recht auf Berichtigung und Ergänzung.
                            </P>
                            <P $size="medium" as="li">
                                Sie können jederzeit die Löschung Ihrer personenbezogenen Daten verlangen, sofern wir nicht rechtlich zur weiteren Verarbeitung Ihrer Daten verpflichtet oder berechtigt sind.
                            </P>
                            <P $size="medium" as="li">
                                Bei Vorliegen der gesetzlichen Voraussetzungen können Sie eine Einschränkung der Verarbeitung Ihrer personenbezogenen Daten verlangen.
                            </P>
                            <P $size="medium" as="li">
                                Sie haben das Recht gegen die Verarbeitung Widerspruch zu erheben, soweit die Datenverarbeitung zum Zwecke der Direktwerbung oder des Profilings erfolgt.
                            </P>
                            <P $size="medium" as="li">
                                Erfolgt die Verarbeitung auf Grund einer Interessenabwägung, so können Sie der Verarbeitung unter Angabe von Gründen, die sich aus Ihrer besonderen Situation ergeben, widersprechen.
                            </P>
                            <P $size="medium" as="li">Erfolgt die Datenverarbeitung auf Grundlage Ihrer Einwilligung oder im Rahmen eines Vertrages, so haben Sie ein Recht auf Übertragung der von Ihnen bereitgestellten Daten, sofern dadurch nicht die Rechte und Freiheiten anderer Personen beeinträchtigt werden.</P>
                            <P $size="medium" as="li">Sofern wir Ihre Daten auf Grundlage einer Einwilligungserklärung verarbeiten, haben Sie jederzeit das Recht, diese Einwilligung mit Wirkung für die Zukunft zu widerrufen. Die vor einem Widerruf durchgeführte Verarbeitung bleibt von dem Widerruf unberührt.</P>
                            <P $size="medium" as="li">Sie haben außerdem jederzeit das Recht, bei einer Aufsichtsbehörde für den Datenschutz Beschwerde einzulegen, wenn Sie der Auffassung sind, dass eine Datenverarbeitung unter Verstoß gegen geltendes Recht erfolgt ist.</P>
                        </NumberedList>
                        <br />
                        <br />
                        <br />
                        <br />
                        <P $size="medium">
                            <Color $color={colors.secondary}>Stand 02. Juli 2024</Color>
                        </P>
                    </Col>
                </StyledRow>
            </Container>
        </Section>
    );
};

/**
 * `getLayout` is a static property of the `Impressum` component that is a function designed to wrap the component with a specified layout.
 * It receives the router object, page properties, and the PageComponent and returns the PageComponent wrapped with the necessary layout and properties.
 * This property is essential for applying consistent layouts across different pages in the application.
 *
 * @param router        The Next.js router object representing the current route of the application.
 * @param pageProps     The page properties object representing the properties of the current page.
 * @param PageComponent The PageComponent representing the current page.
 * @returns A ReactNode representing the PageComponent wrapped with the necessary layout and properties.
 *
 * @example
 * ```tsx
 * const LayoutWrappedComponent = Impressum.getLayout(router, pageProps, PageComponent);
 * ```
 */
Impressum.getLayout = (router, pageProps, PageComponent) => (
    <SonosLayout testId="datenschutzPageLayout" title="Sonos Ace | Datenschutz">
        <PageComponent router={router} {...pageProps} />
    </SonosLayout>
);

/**
 * `getLayoutKey` is a static property of the `Impressum` component that is a function designed to return the layout key string.
 * It is used to determine if the layout changed between pages to apply transitions between different layouts.
 * This property is crucial for managing different layouts within the application and for providing a specific layout key for the `Impressum` component.
 *
 * @returns A string representing the layout key.
 *
 * @example
 * ```tsx
 * const layoutKey = Impressum.getLayoutKey();
 * ```
 */
Impressum.getLayoutKey = () => '';

export default Impressum;

const StyledRow = styled(Row)`
    row-gap: 6rem;
`;